import { Text, Tooltip } from "@mantine/core";
import { useMemo } from "react";

/**
 * Convert a date to a relative time string, such as
 * "a minute ago", "in 2 hours", "yesterday", "3 months ago", etc.
 * using Intl.RelativeTimeFormat
 *
 * @author https://gist.github.com/LewisJEllis/9ad1f35d102de8eee78f6bd081d486ad
 */
export function getRelativeTime(date: Date | number, lang = navigator.language): string {
    if (isNaN(+date)) return "Invalid Date";

    // Allow dates or times to be passed
    const timeMs = typeof date === "number" ? date : date.getTime();

    // Get the amount of seconds between the given date and now
    const deltaSeconds = Math.round((timeMs - Date.now()) / 1000);

    // Array reprsenting one minute, hour, day, week, month, etc in seconds
    const cutoffs = [60, 3600, 86400, 86400 * 7, 86400 * 30, 86400 * 365, Infinity];

    // Array equivalent to the above but in the string representation of the units
    const units: Intl.RelativeTimeFormatUnit[] = ["second", "minute", "hour", "day", "week", "month", "year"];

    // Grab the ideal cutoff unit
    const unitIndex = cutoffs.findIndex((cutoff) => cutoff > Math.abs(deltaSeconds));

    // Get the divisor to divide from the seconds. E.g. if our unit is "day" our divisor
    // is one day in seconds, so we can divide our seconds by this to get the # of days
    const divisor = unitIndex ? cutoffs[unitIndex - 1] : 1;

    // Intl.RelativeTimeFormat do its magic
    const rtf = new Intl.RelativeTimeFormat(lang, { numeric: "auto" });
    return rtf.format(Math.floor(deltaSeconds / divisor), units[unitIndex]);
}

export const RelativeTime = ({ date, withTime }: { date: Date | number; withTime?: boolean }) => {
    const time = useMemo(() => getRelativeTime(date), [date]);
    const d = new Date(date);
    const opts = {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric",
    } as const;

    return (
        <Tooltip label={withTime ? new Date(date).toLocaleTimeString("en-US", opts) : d.toLocaleDateString("en-US", opts)}>
            <Text style={{ textWrap: "nowrap" }}>{time}</Text>
        </Tooltip>
    );
};
